import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  FileJson,
  Type,
  Image as ImageIcon,
  Layers,
  Copy,
  Check,
  Trash2,
} from "lucide-react";
import { getApiUrl } from "../config/api";

interface TemplateElement {
  type: "background" | "sticker" | "text";
  pm_title?: string;
  pm_fontName?: string;
  pm_fontSize?: number;
  pm_textColor?: { Red: number; Green: number; Blue: number };
  pm_layerUrl?: string;
  pm_dimensions?: { width: number; height: number };
  pm_backgroundColor?: { Red: number; Green: number; Blue: number };
  pm_center?: string;
  pm_frame?: string;
  pm_rotation?: string;
  pm_opacity?: string;
  pm_isMasking?: boolean;
  [key: string]: unknown;
}

interface TemplateData {
  _id: string;
  name: string;
  category: { _id: string; name: string; slug: string };
  subcategory: { _id: string; name: string; slug: string };
  keywords: string;
  seoKeywords: string[];
  thumbUrl: string;
  layoutSize: string;
  json_data: TemplateElement[][];
  createdAt: string;
  updatedAt: string;
}

interface TemplateDetailResponse {
  status: string;
  data: TemplateData;
  message: string;
  technicalDetails: Record<string, unknown>;
}

const TemplateDetailScreen: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();

  const [template, setTemplate] = useState<TemplateData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [imageToggleStates, setImageToggleStates] = useState<
    Record<number, boolean>
  >({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    element: TemplateElement;
    index: number;
  } | null>(null);
  const [updatingTemplate, setUpdatingTemplate] = useState(false);

  useEffect(() => {
    const fetchTemplateDetail = async () => {
      if (!templateId) {
        setError("Template ID is missing");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const url = getApiUrl(`/api/templates/v1.0.0/template/${templateId}`);
        console.log("Fetching template from:", url);

        const response = await fetch(url, {
          method: "GET",
          headers: {
            accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result: TemplateDetailResponse = await response.json();
        console.log("Template response:", result);

        if (result.status === "success" && result.data) {
          setTemplate(result.data);

          // Initialize image toggle states based on pm_isMasking values
          const initialToggleStates: Record<number, boolean> = {};
          result.data.json_data[0]?.forEach((element, index) => {
            if (element.type === "sticker") {
              initialToggleStates[index] = Boolean(element.pm_isMasking);
            }
          });
          setImageToggleStates(initialToggleStates);
        } else {
          setError(result.message || "Failed to fetch template");
        }
      } catch (err) {
        console.error("Error fetching template:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load template"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchTemplateDetail();
  }, [templateId]);

  const handleBack = () => {
    navigate(-1);
  };

  const updateTemplate = async (updatedJsonData: TemplateElement[][]) => {
    if (!template || !templateId) return;

    try {
      setUpdatingTemplate(true);
      const url = getApiUrl(`/api/templates/v1.0.0/template/${templateId}`);

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: template.name,
          category: template.category._id,
          subcategory: template.subcategory._id,
          keywords: template.keywords,
          categorySlug: template.category.slug,
          seoKeywords: template.seoKeywords,
          json_data: updatedJsonData,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status === "success") {
        // Update local state with the new data
        setTemplate((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            json_data: updatedJsonData,
          };
        });

        // Update image toggle states based on the new data
        const updatedToggleStates: Record<number, boolean> = {};
        updatedJsonData[0]?.forEach((element, index) => {
          if (element.type === "sticker") {
            updatedToggleStates[index] = Boolean(element.pm_isMasking);
          }
        });
        setImageToggleStates(updatedToggleStates);

        console.log("Template updated successfully");
      } else {
        throw new Error(result.message || "Failed to update template");
      }
    } catch (err) {
      console.error("Error updating template:", err);
      alert(
        err instanceof Error
          ? err.message
          : "Failed to update template. Please try again."
      );
    } finally {
      setUpdatingTemplate(false);
    }
  };

  const rgbToHex = (rgb: {
    Red: number;
    Green: number;
    Blue: number;
  }): string => {
    const r = rgb.Red.toString(16).padStart(2, "0");
    const g = rgb.Green.toString(16).padStart(2, "0");
    const b = rgb.Blue.toString(16).padStart(2, "0");
    return `#${r}${g}${b}`;
  };

  const copyToClipboard = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleDelete = (element: TemplateElement, index: number) => {
    setItemToDelete({ element, index });
    setShowDeleteModal(true);
  };

  const confirmDeleteItem = async () => {
    if (!itemToDelete || !template) return;

    try {
      // Create a copy of the json_data and remove the item
      const updatedJsonData = template.json_data.map((layer) =>
        layer.filter((_, itemIndex) => itemIndex !== itemToDelete.index)
      );

      // Update the template via API
      await updateTemplate(updatedJsonData);

      // Close modal
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (err) {
      console.error("Error deleting item:", err);
    }
  };

  const handleImageToggle = async (index: number) => {
    if (!template) return;

    const newToggleState = !imageToggleStates[index];
    setImageToggleStates((prev) => ({
      ...prev,
      [index]: newToggleState,
    }));

    // Update the template JSON with isMasking property
    const updatedJsonData = template.json_data.map((layer) =>
      layer.map((item, itemIndex) => {
        if (itemIndex === index && item.type === "sticker") {
          return {
            ...item,
            pm_isMasking: newToggleState,
          };
        }
        return item;
      })
    );

    // Update the template via API
    await updateTemplate(updatedJsonData);
  };

  const renderElement = (element: TemplateElement, index: number) => {
    const elementKey = `element-${index}`;

    if (element.type === "text") {
      return (
        <div
          key={elementKey}
          className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Type className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Text Element</h3>
                <p className="text-sm text-gray-500">#{index + 1}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => copyToClipboard(element.pm_title || "", index)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Copy text"
              >
                {copiedIndex === index ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4 text-gray-600" />
                )}
              </button>
              <button
                onClick={() => handleDelete(element, index)}
                className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                title="Delete item"
              >
                <Trash2 className="w-4 h-4 text-red-600" />
              </button>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <span className="text-sm font-medium text-gray-700">Text:</span>
              <p className="mt-1 text-gray-900 font-medium bg-gray-50 p-3 rounded-lg whitespace-pre-wrap">
                {element.pm_title}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm font-medium text-gray-700">
                  Font Family:
                </span>
                <p className="text-gray-900">{element.pm_fontName}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">
                  Font Size:
                </span>
                <p className="text-gray-900">
                  {element.pm_fontSize?.toFixed(4)}
                </p>
              </div>
            </div>

            {element.pm_textColor && (
              <div>
                <span className="text-sm font-medium text-gray-700">
                  Text Color:
                </span>
                <div className="flex items-center gap-2 mt-1">
                  <div
                    className="w-8 h-8 rounded border border-gray-300"
                    style={{
                      backgroundColor: rgbToHex(element.pm_textColor),
                    }}
                  ></div>
                  <span className="text-gray-900 font-mono text-sm">
                    {rgbToHex(element.pm_textColor)}
                  </span>
                  <span className="text-gray-500 text-sm">
                    RGB({element.pm_textColor.Red}, {element.pm_textColor.Green}
                    , {element.pm_textColor.Blue})
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (element.type === "sticker") {
      return (
        <div
          key={elementKey}
          className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <ImageIcon className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Image/Sticker</h3>
                <p className="text-sm text-gray-500">#{index + 1}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">
                Masking {imageToggleStates[index] ? "Disable:" : "Enable:"}
              </span>
              <button
                onClick={() => handleImageToggle(index)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  imageToggleStates[index] ? "bg-indigo-600" : "bg-gray-300"
                }`}
                role="switch"
                aria-checked={imageToggleStates[index]}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    imageToggleStates[index] ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
              <button
                onClick={() => handleDelete(element, index)}
                className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                title="Delete item"
              >
                <Trash2 className="w-4 h-4 text-red-600" />
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {element.pm_layerUrl && (
              <div>
                <span className="text-sm font-medium text-gray-700">
                  Preview:
                </span>
                <div className="mt-2 bg-gray-100 rounded-lg p-4 flex justify-center">
                  <img
                    src={element.pm_layerUrl}
                    alt={`Sticker ${index + 1}`}
                    className="max-h-40 rounded"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Crect fill='%23e5e7eb' width='200' height='200'/%3E%3Ctext fill='%239ca3af' font-family='sans-serif' font-size='16' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3EImage Error%3C/text%3E%3C/svg%3E";
                    }}
                  />
                </div>
              </div>
            )}

            {element.pm_dimensions && (
              <div>
                <span className="text-sm font-medium text-gray-700">
                  Dimensions:
                </span>
                <p className="text-gray-900">
                  {element.pm_dimensions.width} × {element.pm_dimensions.height}
                  px
                </p>
              </div>
            )}

            {element.pm_layerUrl && (
              <div>
                <span className="text-sm font-medium text-gray-700">URL:</span>
                <p className="text-gray-600 text-sm font-mono break-all bg-gray-50 p-2 rounded">
                  {element.pm_layerUrl}
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (element.type === "background") {
      return (
        <div
          key={elementKey}
          className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Layers className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Background</h3>
                <p className="text-sm text-gray-500">#{index + 1}</p>
              </div>
            </div>
            <button
              onClick={() => handleDelete(element, index)}
              className="p-2 hover:bg-red-100 rounded-lg transition-colors"
              title="Delete item"
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </button>
          </div>

          {element.pm_backgroundColor && (
            <div>
              <span className="text-sm font-medium text-gray-700">
                Background Color:
              </span>
              <div className="flex items-center gap-2 mt-2">
                <div
                  className="w-12 h-12 rounded border border-gray-300"
                  style={{
                    backgroundColor: rgbToHex(element.pm_backgroundColor),
                  }}
                ></div>
                <div>
                  <p className="text-gray-900 font-mono text-sm">
                    {rgbToHex(element.pm_backgroundColor)}
                  </p>
                  <p className="text-gray-500 text-sm">
                    RGB({element.pm_backgroundColor.Red},{" "}
                    {element.pm_backgroundColor.Green},{" "}
                    {element.pm_backgroundColor.Blue})
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl shadow-lg border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center space-x-4 flex-1">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <FileJson className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  Template Details
                </h1>
                {template && (
                  <p className="text-sm text-gray-500 mt-1">
                    {template.name} • {template.category.name} →{" "}
                    {template.subcategory.name}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {(() => {
          if (loading) {
            return (
              <div className="flex justify-center items-center h-96">
                <div className="text-center">
                  <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
                  <p className="text-gray-600">Loading template details...</p>
                </div>
              </div>
            );
          }
          if (error) {
            return (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileJson className="w-12 h-12 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Error Loading Template
                </h3>
                <p className="text-gray-500 mb-4">{error}</p>
                <button
                  onClick={() => globalThis.location.reload()}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-medium transition-colors"
                >
                  Retry
                </button>
              </div>
            );
          }
          if (template) {
            return (
              <div className="space-y-6">
            {/* Template Info Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="grid md:grid-cols-3 gap-6 p-6">
                {/* Thumbnail */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    Thumbnail
                  </h3>
                  <img
                    src={template.thumbUrl}
                    alt={template.name}
                    className="w-full rounded-lg border border-gray-200"
                  />
                </div>

                {/* Basic Info */}
                <div className="md:col-span-2 space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-1">
                      Name
                    </h3>
                    <p className="text-gray-900 font-medium">{template.name}</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-1">
                        Category
                      </h3>
                      <p className="text-gray-900">{template.category.name}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-1">
                        Subcategory
                      </h3>
                      <p className="text-gray-900">
                        {template.subcategory.name}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-1">
                      Layout Size
                    </h3>
                    <p className="text-gray-900">{template.layoutSize}</p>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-1">
                      Keywords
                    </h3>
                    <p className="text-gray-900">{template.keywords}</p>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-1">
                      SEO Keywords
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {template.seoKeywords.map((keyword, idx) => (
                        <span
                          key={`${keyword}-${idx}`}
                          className="px-3 py-1 bg-indigo-100 text-indigo-700 text-sm rounded-full"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* JSON Data Elements */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Template Elements ({template.json_data[0]?.length || 0})
              </h2>

              <div className="space-y-4">
                {template.json_data[0]?.map((element, index) =>
                  renderElement(element, index)
                )}
              </div>
            </div>
          </div>
            );
          }
          return null;
        })()}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Delete Item
              </h3>
            </div>

            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this {itemToDelete.element.type}{" "}
              item? This action cannot be undone.
            </p>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setItemToDelete(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                disabled={updatingTemplate}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteItem}
                disabled={updatingTemplate}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updatingTemplate ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateDetailScreen;
